// src/components/Carousel.jsx
import React, { useState, useEffect } from 'react';

const Carousel = ({
  items,
  autoScroll = true,
  interval = 6000,
  className = '',
  showControls = true,
  showIndicators = true,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!autoScroll || items.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % items.length);
    }, interval);

    return () => clearInterval(timer);
  }, [autoScroll, interval, items.length]);

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + items.length) % items.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % items.length);
  };

  return (
    <div className={`relative w-full overflow-hidden rounded-2xl ${className}`}>
      {/* Slides Container - Classic 2D Horizontal Slide */}
      <div
        className="flex transition-transform duration-1000 ease-in-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {items.map((item, index) => (
          <div key={index} className="w-full flex-shrink-0">
            {/* Each slide takes full width and centers the Card3D */}
            <div className="px-4 py-8 md:px-8 lg:px-16">
              <div className="max-w-5xl mx-auto">
                {item}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Clean Professional Navigation Arrows */}
      {showControls && items.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full p-3 md:p-4 shadow-xl transition-all duration-300 hover:shadow-2xl"
            aria-label="Previous case study"
          >
            <svg className="w-6 h-6 md:w-7 md:h-7 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={goToNext}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 bg-white/90 hover:bg-white rounded-full p-3 md:p-4 shadow-xl transition-all duration-300 hover:shadow-2xl"
            aria-label="Next case study"
          >
            <svg className="w-6 h-6 md:w-7 md:h-7 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      {/* Minimal Elegant Indicators */}
      {showIndicators && items.length > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-3 z-10">
          {items.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 ${
                index === currentIndex
                  ? 'w-10 h-2 bg-white/60 rounded-full shadow-md'
                  : 'w-2 h-2 bg-blue-950 hover:bg-white/90 rounded-full'
              }`}
              aria-label={`Go to case study ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Carousel;